import { DateTimePicker, DateTimePickerProps } from "@mui/x-date-pickers";
import { Dayjs } from "dayjs";
import { GET_CONFIG } from "../../routes/config/graphql";
import { useQuery } from "@apollo/client";
import { DateTime, Settings } from "luxon";
import { GET_EDITION } from "../../routes/editionsBands/graphql";
import { PropsWithChildren } from "react";

export const useGetZonedBookingTimes = () => {
  const { timezone } = useGetTimezone();

  return {
    updateZonedBookingTimes: (startTime: string, endTime: string) => {
      Settings.defaultZone = timezone;

      const luxonStartDate = DateTime.fromISO(startTime, { setZone: true });
      const luxonEndDate = DateTime.fromISO(endTime, { setZone: true });

      return {
        startTime, //: luxonStartDate!.toISO()!,
        endTime,
        // @ts-ignore
        date: luxonStartDate?.startOf("day") as string,
      };
      //
    },
  };
};

export const useGetTimezone = () => {
  const { data, refetch, loading, error } = useQuery(GET_CONFIG);

  const result = data?.configs?.reduce(
    (prev: { [x: string]: any }, next: { key: any; value: any }) => {
      const { key, value } = next;

      prev[key] = value;
      return prev;
    },
    {}
  );

  const timezone = result?.TIMEZONE;

  return { timezone, loading, error };
};

type Props = DateTimePickerProps<Dayjs> &
  React.RefAttributes<HTMLDivElement>;
export const ZonedDateTimePicker: React.FC<PropsWithChildren<Props>> = (
  allProps
) => {
  const { timezone, loading, error } = useGetTimezone();
  const { children, ...props } = allProps;

  if (loading) return <p>Loading</p>;

  if (!timezone)
    return (
      <p style={{ color: "red" }}>
        Timezone is missing, please add it on the config page
      </p>
    );

  return (
    <div
      style={{
        // border: "1px solid",
        // borderColor: "rgba(0, 0, 0, 0.23)",
        gap: 8,
        display: "flex",
        flexDirection: "column",
        paddingTop: 16,
        paddingBottom: 16,
        borderRadius: 12,
        marginBottom: 16,
      }}
    >
      <DateTimePicker
        {...props}
        onChange={(args) => {
          if (!props.onChange) return;
          if (!args) {
            // @ts-ignore
            props.onChange(null);
            return;
          }
          // args.format() returns the time as displayed to the user in the
          // festival timezone. Re-interpret that as a festival-timezone ISO
          // string so the value is correct regardless of browser timezone.
          const iso = DateTime.fromISO(
            args.format("YYYY-MM-DDTHH:mm:ss"),
            { zone: timezone }
          ).toISO();
          // @ts-ignore
          props.onChange(iso);
        }}
        slotProps={{
          textField: {
            inputProps: {
              placeholder: 'YYYY/MM/DD HH:mm'
            }
          }
        }}
        timezone={timezone}
      />
      <label
        style={{ marginLeft: 8, marginTop: -20 }}
      >{`In timezone "${timezone}"`}</label>
      {children}
    </div>
  );
};

export const getZonedDate = (
  _date: string,
  toISO: boolean = false,
  timezone: string = "Europe/London"
) => {
  Settings.defaultZone = timezone;

  try {
    const date = DateTime.fromISO(_date, { setZone: true });

    if (!date.isValid) {
      throw new Error("unparsable date");
    }

    const res = date.setZone(timezone, {
      keepLocalTime: true,
    });

    if (toISO) {
      return res.toISO();
    }

    return res.toJSDate();
  } catch (ex) {
    console.log("ex", ex);
    return DateTime.fromJSDate(new Date(_date), { zone: timezone })
      .setZone(timezone, {
        keepLocalTime: true,
      })
      .toISO();
  }
};

export const useGetDisplayDate = () => {
  return {
    getFormattedDate: (date: string | undefined | null, timezone: string) => {
      if (!date) return date;

      return DateTime.fromISO(getZonedDate(date, true, timezone) as string, {
        setZone: true,
      }).toFormat("HH:mm");
    },
    getDateInFormat: (date: string, timezone: string, format: string) => {
      return DateTime.fromISO(getZonedDate(date, true, timezone) as string, {
        setZone: true,
      }).toFormat(format);
    },
  };
};

// requires fake date
// band bookings

// requires real date
// news - post date
// edition settings / available in app on date
// notification scheduling date
// app settings / countdown date
